import { Form } from 'react-bootstrap';
import {
  getIpawsCharacterCountState,
  IPAWS_PUBLIC_MESSAGE_LIMITS,
} from '../../../../utils/ipawsLanguage.utils';
import IpawsFieldFindings from '../IpawsFieldFindings/IpawsFieldFindings';
import IpawsTextField from '../IpawsTextField/IpawsTextField';

const WEA_LIMITS = { short: 90, long: 360 }; // Interim until BE-14 serves these.
const getParameter = (items, name) =>
  (items ?? []).find((item) => item.valueName === name)?.value ?? '';
const setParameter = (items, name, value) => [
  ...(items ?? []).filter((item) => item.valueName !== name),
  ...(value ? [{ valueName: name, value }] : []),
];

const Counter = ({ value, limit, id }) => {
  const { count, tone } = getIpawsCharacterCountState(value, limit);
  return (
    <small
      id={id}
      className={`alert-creator-character-count d-block text-end ${tone === 'over' ? 'text-danger' : tone === 'warning' ? 'text-warning' : ''}`}
      aria-live="polite"
    >
      {count}/{limit} Characters
      {tone === 'over' ? ` — ${count - limit} over` : ''}
    </small>
  );
};

const Block = ({
  name,
  code,
  values,
  prefix = '',
  findings,
  hasWea,
  hasPublicMessageLimits = false,
  onChange,
  onWeaChange,
  review,
  showLanguageLegend = true,
  showLanguageSpecificContext = false,
}) => {
  const isSpanish = code === 'es-US';
  const path = (field) => (prefix ? `${prefix}.${field}` : field);
  const short = values.weaShortText ?? '';
  const long = values.weaLongText ?? '';
  const fields = (
    <>
      {showLanguageSpecificContext ? (
        <>
          <IpawsTextField
            controlId={`ipaws-alert-${code}-event`}
            fieldPath={path('event')}
            label="Evento"
            value={values.event}
            onChange={({ target }) => onChange('event', target.value)}
            findings={findings}
            placeholder="Evacuación por incendio forestal"
            required
          />
          <IpawsTextField
            controlId={`ipaws-alert-${code}-area-description`}
            fieldPath={path('areaDescription')}
            label="Descripción del área afectada"
            value={values.areaDescription}
            onChange={({ target }) => onChange('areaDescription', target.value)}
            findings={findings}
            placeholder="Describa el área afectada en español"
            as="textarea"
            rows={3}
            required
          />
        </>
      ) : null}
      <IpawsTextField
        controlId={`ipaws-alert-${code}-headline`}
        fieldPath={path('headline')}
        label={isSpanish ? 'Titular' : 'Headline'}
        value={values.headline}
        onChange={({ target }) => onChange('headline', target.value)}
        findings={findings}
        describedBy={hasPublicMessageLimits ? `ipaws-alert-${code}-headline-count` : undefined}
        maxLength={hasPublicMessageLimits ? IPAWS_PUBLIC_MESSAGE_LIMITS.headline : undefined}
        controlClassName="alert-creator-message-control alert-creator-message-control-headline"
        required
      >
        {hasPublicMessageLimits ? (
          <Counter
            value={values.headline}
            limit={IPAWS_PUBLIC_MESSAGE_LIMITS.headline}
            id={`ipaws-alert-${code}-headline-count`}
          />
        ) : null}
      </IpawsTextField>
      <IpawsTextField
        controlId={`ipaws-alert-${code}-description`}
        fieldPath={path('description')}
        label={isSpanish ? 'Descripción' : 'Description'}
        value={values.description}
        onChange={({ target }) => onChange('description', target.value)}
        findings={findings}
        describedBy={hasPublicMessageLimits ? `ipaws-alert-${code}-description-count` : undefined}
        maxLength={hasPublicMessageLimits ? IPAWS_PUBLIC_MESSAGE_LIMITS.description : undefined}
        controlClassName="alert-creator-message-control alert-creator-message-control-standard"
        as="textarea"
        rows={5}
        required
      >
        {hasPublicMessageLimits ? (
          <Counter
            value={values.description}
            limit={IPAWS_PUBLIC_MESSAGE_LIMITS.description}
            id={`ipaws-alert-${code}-description-count`}
          />
        ) : null}
      </IpawsTextField>
      <IpawsTextField
        controlId={`ipaws-alert-${code}-instruction`}
        fieldPath={path('instruction')}
        label={isSpanish ? 'Instrucciones de protección' : 'Protective Action Instructions'}
        value={values.instruction}
        onChange={({ target }) => onChange('instruction', target.value)}
        findings={findings}
        describedBy={hasPublicMessageLimits ? `ipaws-alert-${code}-instruction-count` : undefined}
        maxLength={hasPublicMessageLimits ? IPAWS_PUBLIC_MESSAGE_LIMITS.instruction : undefined}
        controlClassName="alert-creator-message-control alert-creator-message-control-standard"
        as="textarea"
        rows={4}
        required
      >
        {hasPublicMessageLimits ? (
          <Counter
            value={values.instruction}
            limit={IPAWS_PUBLIC_MESSAGE_LIMITS.instruction}
            id={`ipaws-alert-${code}-instruction-count`}
          />
        ) : null}
      </IpawsTextField>
      {hasWea ? (
        <>
          <IpawsTextField
            controlId={`ipaws-alert-${code}-wea-short`}
            fieldPath={path(prefix ? 'weaShortText' : 'CMAMtext')}
            label={isSpanish ? 'Mensaje WEA — 90 caracteres' : 'WEA Message — 90 characters'}
            value={short}
            onChange={({ target }) => onWeaChange('short', target.value)}
            findings={findings}
            describedBy={`ipaws-alert-${code}-wea-short-count`}
            maxLength={WEA_LIMITS.short}
            controlClassName="alert-creator-message-control alert-creator-message-control-wea-short"
            required
          >
            <Counter
              value={short}
              limit={WEA_LIMITS.short}
              id={`ipaws-alert-${code}-wea-short-count`}
            />
          </IpawsTextField>
          <IpawsTextField
            controlId={`ipaws-alert-${code}-wea-long`}
            fieldPath={path(prefix ? 'weaLongText' : 'CMAMlongtext')}
            label={isSpanish ? 'Mensaje WEA — 360 caracteres' : 'WEA Message — 360 characters'}
            value={long}
            onChange={({ target }) => onWeaChange('long', target.value)}
            findings={findings}
            describedBy={`ipaws-alert-${code}-wea-long-count`}
            maxLength={WEA_LIMITS.long}
            controlClassName="alert-creator-message-control alert-creator-message-control-wea-long"
            as="textarea"
            rows={4}
            required
          >
            <Counter
              value={long}
              limit={WEA_LIMITS.long}
              id={`ipaws-alert-${code}-wea-long-count`}
            />
          </IpawsTextField>
        </>
      ) : null}
      {review}
    </>
  );

  if (!showLanguageLegend) {
    return <div className="alert-creator-public-message-fields">{fields}</div>;
  }

  return (
    <fieldset className="alert-creator-language-block border rounded-3 p-3">
      <legend className="alert-creator-control-label px-1">
        {name} <span className="text-secondary">({code})</span>
      </legend>
      {fields}
    </fieldset>
  );
};

const IpawsPublicMessageSection = ({ draft, findings, onDraftChange, onTextChange }) => {
  const spanish = draft.languages?.[0];
  const hasWea = draft.deliveryChannels?.includes('CMAS');
  const updateSpanish = (field, value) =>
    onDraftChange({ languages: [{ ...spanish, [field]: value, isReviewed: false }] });
  const updateEnglishWea = (size, value) =>
    onDraftChange({
      parameters: setParameter(
        draft.parameters,
        size === 'short' ? 'CMAMtext' : 'CMAMlongtext',
        value,
      ),
    });
  return (
    <fieldset className="alert-creator-section alert-creator-section-ipaws alert-creator-public-message border rounded-3 p-3">
      <legend className="alert-creator-section-title d-flex align-items-center">
        Public message
      </legend>
      <Form.Check
        id="ipaws-alert-language-mode"
        className="alert-creator-language-toggle"
        type="switch"
        label="Spanish / Español – ES-US"
        checked={Boolean(spanish)}
        onChange={({ target }) =>
          onDraftChange({
            languages: target.checked
              ? [
                  spanish ?? {
                    language: 'es-US',
                    event: '',
                    areaDescription: draft.areaDesc,
                    web: '',
                    contact: '',
                    headline: '',
                    description: '',
                    instruction: '',
                    weaShortText: '',
                    weaLongText: '',
                    isReviewed: false,
                  },
                ]
              : [],
          })
        }
      />
      <div className="alert-creator-language-grid">
        <Block
          name="English"
          code="en-US"
          values={{
            headline: draft.headline,
            description: draft.description,
            instruction: draft.instruction,
            weaShortText: getParameter(draft.parameters, 'CMAMtext'),
            weaLongText: getParameter(draft.parameters, 'CMAMlongtext'),
          }}
          findings={findings}
          hasWea={hasWea}
          hasPublicMessageLimits
          showLanguageLegend={false}
          onChange={(field, value) => onTextChange({ target: { name: field, value } })}
          onWeaChange={updateEnglishWea}
        />
        {spanish ? (
          <Block
            name="Español"
            code="es-US"
            values={spanish}
            prefix="languages.0"
            showLanguageSpecificContext
            findings={findings}
            hasWea={hasWea}
            hasPublicMessageLimits
            onChange={updateSpanish}
            onWeaChange={(size, value) =>
              updateSpanish(size === 'short' ? 'weaShortText' : 'weaLongText', value)
            }
            review={
              <div>
                <Form.Check
                  id="ipaws-alert-es-US-reviewed"
                  type="checkbox"
                  label="He revisado y acepto el texto en español"
                  checked={Boolean(spanish.isReviewed)}
                  onChange={({ target }) =>
                    onDraftChange({ languages: [{ ...spanish, isReviewed: target.checked }] })
                  }
                />
                <IpawsFieldFindings fieldPath="languages.0.isReviewed" findings={findings} />
              </div>
            }
          />
        ) : null}
      </div>
      <IpawsTextField
        controlId="ipaws-alert-public-information-url"
        fieldPath="web"
        label="Public information URL"
        value={draft.web}
        onChange={onTextChange}
        findings={findings}
        placeholder="https://www.example.gov/emergency"
      />
      <IpawsTextField
        controlId="ipaws-alert-public-contact"
        fieldPath="contact"
        label="Public contact"
        value={draft.contact}
        onChange={onTextChange}
        findings={findings}
        placeholder="Agency name and public phone number"
      />
      {spanish ? (
        <>
          <IpawsTextField
            controlId="ipaws-alert-es-US-web"
            fieldPath="languages.0.web"
            label="URL de información pública en español (opcional)"
            value={spanish.web}
            onChange={({ target }) => updateSpanish('web', target.value)}
            findings={findings}
            placeholder="https://www.example.gov/es/emergencia"
          />
          <IpawsTextField
            controlId="ipaws-alert-es-US-contact"
            fieldPath="languages.0.contact"
            label="Contacto público en español (opcional)"
            value={spanish.contact}
            onChange={({ target }) => updateSpanish('contact', target.value)}
            findings={findings}
            placeholder="Nombre de la agencia y teléfono público"
          />
        </>
      ) : null}
    </fieldset>
  );
};
export default IpawsPublicMessageSection;
